import { createTherapeuticBookProduct, searchMedicamentReference } from "../../../services/api";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { FieldLabel } from "../AdminCommon";
import {
    therapeuticModalStyle,
    therapeuticModalTitleStyle,
    therapeuticSelectedReferenceStyle,
    therapeuticSuccessStyle,
    therapeuticSuggestionButtonStyle,
    therapeuticSuggestionsStyle,
} from "../styles/adminDashboardFormStyles";
import {
    errorStyle,
    fieldStyle,
    modalBackdropStyle,
    modalCloseButtonStyle,
    modalHeaderStyle,
    modalSecondaryButtonStyle,
    therapeuticFormGridStyle,
    therapeuticInputStyle,
    therapeuticPrimaryButtonStyle,
    therapeuticSecondaryButtonStyle,
} from "../styles/adminDashboardStyles";
import { THERAPEUTIC_PRODUCT_CLASS_OPTIONS, firstProductCode } from "./therapeuticRules";
import ModalBackdrop from "../../../components/ModalBackdrop";

// Recherche dans le référentiel des médicaments à partir de 2 caractères, tant qu'aucune référence n'est choisie.
function useMedicamentReferenceSearch(query, selectedReference) {
    const [suggestions, setSuggestions] = useState([]);
    const [searching, setSearching] = useState(false);
    useEffect(() => {
        const q = query.trim();
        if (selectedReference || q.length < 2) {
            setSuggestions([]);
            setSearching(false);
            return undefined;
        }
        setSearching(true);
        const timer = setTimeout(() => {
            searchMedicamentReference(q)
                .then(({ data }) => setSuggestions(data || []))
                .catch(() => setSuggestions([]))
                .finally(() => setSearching(false));
        }, 220);
        return () => clearTimeout(timer);
    }, [query, selectedReference]);
    return { suggestions, setSuggestions, searching };
}

function ReferenceSearchField({ query, suggestions, searching, onQueryChange, onSelect }) {
    return (
        <div style={{ position: "relative" }}>
            <FieldLabel htmlFor="therapeutic-book-product-name" required>Produit</FieldLabel>
            <input
                id="therapeutic-book-product-name"
                value={query}
                onChange={event => onQueryChange(event.target.value)}
                placeholder="Nom ou code CIP13"
                autoComplete="off"
                style={therapeuticInputStyle}
            />
            {suggestions.length > 0 && (
                <div style={therapeuticSuggestionsStyle}>
                    {suggestions.map(reference => (
                        <button
                            key={reference.codeCis}
                            type="button"
                            onClick={() => onSelect(reference)}
                            style={therapeuticSuggestionButtonStyle}
                        >
                            <span style={{ fontWeight: 800, color: "#122A26" }}>{reference.denomination}</span>
                            {firstProductCode(reference.cip13Codes) && (
                                <span style={{ fontSize: "12px", color: "#5A6D68" }}>
                                    CIP13 {firstProductCode(reference.cip13Codes)}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            )}
            {searching && <p style={{ margin: "6px 0 0", color: "#5A6D68", fontSize: "12px" }}>Recherche...</p>}
        </div>
    );
}

// Code, classe, unité thérapeutique et quantité par boîte ; la classe est imposée pour un médicament du référentiel.
function ProductFields({ form, selectedReference, onFieldChange }) {
    return (
        <>
            <div style={therapeuticFormGridStyle}>
                <div style={fieldStyle}>
                    <FieldLabel htmlFor="therapeutic-book-product-code" required>
                        {selectedReference ? "Code CIP13" : "Code ACL13 ou CIP13"}
                    </FieldLabel>
                    <input
                        id="therapeutic-book-product-code"
                        value={form.code}
                        placeholder={selectedReference ? "Renseigné automatiquement si connu" : ""}
                        onChange={event => onFieldChange("code", event.target.value)}
                        maxLength={13}
                        inputMode="numeric"
                        style={therapeuticInputStyle}
                    />
                </div>

                <div style={fieldStyle}>
                    <FieldLabel htmlFor="therapeutic-book-product-class" required>Classe</FieldLabel>
                    {selectedReference ? (
                        <input id="therapeutic-book-product-class" value="Médicament" readOnly style={{ ...therapeuticInputStyle, background: "#F4F1EA" }} />
                    ) : (
                        <select
                            id="therapeutic-book-product-class"
                            value={form.productClass}
                            onChange={event => onFieldChange("productClass", event.target.value)}
                            style={therapeuticInputStyle}
                        >
                            <option value="">Sélectionner</option>
                            {THERAPEUTIC_PRODUCT_CLASS_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    )}
                </div>
            </div>

            <div style={therapeuticFormGridStyle}>
                <div style={fieldStyle}>
                    <FieldLabel htmlFor="therapeutic-book-product-unit" required>Unité thérapeutique</FieldLabel>
                    <input
                        id="therapeutic-book-product-unit"
                        value={form.therapeuticUnit}
                        onChange={event => onFieldChange("therapeuticUnit", event.target.value)}
                        placeholder="Sirop, comprimé, gélule..."
                        style={therapeuticInputStyle}
                    />
                </div>

                <div style={fieldStyle}>
                    <FieldLabel htmlFor="therapeutic-book-product-box-quantity" required>Quantité par boîte</FieldLabel>
                    <input
                        id="therapeutic-book-product-box-quantity"
                        value={form.boxQuantity}
                        onChange={event => onFieldChange("boxQuantity", event.target.value)}
                        min="1"
                        inputMode="numeric"
                        type="number"
                        style={therapeuticInputStyle}
                    />
                </div>
            </div>
        </>
    );
}

export function TherapeuticBookProductModal({ onClose, onCreated }) {
    const [query, setQuery] = useState("");
    const [selectedReference, setSelectedReference] = useState(null);
    const { suggestions, setSuggestions, searching } = useMedicamentReferenceSearch(query, selectedReference);
    const [form, setForm] = useState({ name: "", code: "", productClass: "", therapeuticUnit: "", boxQuantity: "" });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const updateField = (field, value) => {
        setSuccessMessage(null);
        setForm(current => ({ ...current, [field]: value }));
    };

    const selectReference = (reference) => {
        const code = firstProductCode(reference.cip13Codes);
        setSelectedReference(reference);
        setQuery(reference.denomination || "");
        setForm({
            name: reference.denomination || "",
            code,
            productClass: "MEDICAMENT",
            therapeuticUnit: reference.formePharmaceutique || "",
            boxQuantity: "",
        });
        setSuggestions([]);
        setError(null);
        setSuccessMessage(null);
    };

    const clearReference = () => {
        setSelectedReference(null);
        setForm(current => ({ ...current, productClass: "", code: "", therapeuticUnit: "", boxQuantity: "" }));
        setSuccessMessage(null);
    };

    const handleQueryChange = (value) => {
        setQuery(value);
        setSuccessMessage(null);
        if (selectedReference) {
            setSelectedReference(null);
            setForm({ name: value, code: "", productClass: "", therapeuticUnit: "", boxQuantity: "" });
        } else {
            setForm(current => ({ ...current, name: value }));
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError(null);
        setSuccessMessage(null);

        const payload = selectedReference
            ? {
                medicamentReferenceCodeCis: selectedReference.codeCis,
                code: form.code,
                therapeuticUnit: form.therapeuticUnit,
                boxQuantity: Number(form.boxQuantity),
            }
            : {
                name: form.name,
                code: form.code,
                productClass: form.productClass,
                therapeuticUnit: form.therapeuticUnit,
                boxQuantity: Number(form.boxQuantity),
            };

        setSubmitting(true);
        try {
            const { data } = await createTherapeuticBookProduct(payload);
            onCreated(data);
            setQuery("");
            setSuggestions([]);
            setSelectedReference(null);
            setForm({ name: "", code: "", productClass: "", therapeuticUnit: "", boxQuantity: "" });
            setSuccessMessage(`${data.name} a été ajouté au livret.`);
        } catch (err) {
            setError(err.response?.data?.message || "Impossible d'ajouter ce produit.");
        } finally {
            setSubmitting(false);
        }
    };

    const hasRequiredPresentation = Boolean(form.therapeuticUnit.trim() && Number(form.boxQuantity) > 0);
    const canSubmit = selectedReference
        ? Boolean(form.code && hasRequiredPresentation)
        : Boolean(form.name.trim() && form.code.trim() && form.productClass && hasRequiredPresentation);

    return (
        <div style={modalBackdropStyle}>
            <ModalBackdrop onClose={() => !submitting && onClose()} />
            <form
                onSubmit={handleSubmit}
                style={{ ...therapeuticModalStyle, position: "relative", zIndex: 1 }}
            >
                <div style={modalHeaderStyle}>
                    <h2 style={therapeuticModalTitleStyle}>Ajouter des produits</h2>
                    <button type="button" onClick={onClose} disabled={submitting} aria-label="Fermer" style={modalCloseButtonStyle}>×</button>
                </div>

                <div style={{ display: "grid", gap: "16px" }}>
                    <ReferenceSearchField
                        query={query}
                        suggestions={suggestions}
                        searching={searching}
                        onQueryChange={handleQueryChange}
                        onSelect={selectReference}
                    />

                    {selectedReference && (
                        <div style={therapeuticSelectedReferenceStyle}>
                            <div>
                                <div style={{ fontSize: "12px", color: "#5A6D68", fontWeight: 800, marginBottom: "3px" }}>Médicament</div>
                                <div style={{ fontSize: "14px", color: "#122A26", fontWeight: 800 }}>{selectedReference.denomination}</div>
                            </div>
                            <button type="button" onClick={clearReference} style={therapeuticSecondaryButtonStyle}>Changer</button>
                        </div>
                    )}

                    <ProductFields form={form} selectedReference={selectedReference} onFieldChange={updateField} />

                    {error && <p style={errorStyle}>{error}</p>}
                    {successMessage && <p style={therapeuticSuccessStyle}>{successMessage}</p>}

                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", paddingTop: "6px" }}>
                        <button type="button" onClick={onClose} disabled={submitting} style={modalSecondaryButtonStyle}>Annuler</button>
                        <button type="submit" disabled={submitting || !canSubmit} style={therapeuticPrimaryButtonStyle}>
                            {submitting ? "Ajout..." : "Ajouter au livret"}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}
TherapeuticBookProductModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    onCreated: PropTypes.func.isRequired,
};
